import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GATEWAYS,
  type GatewayId,
  findGateway,
  gatewayIdForVerseEnv,
  gatewayUrlOf,
  resolveVerseEntry,
} from '@rb/shared/admin-verses';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  GATEWAY_STORAGE_KEY,
  selectEffectiveGatewayId,
  useActiveVerseStore,
} from './active-verse.store';

/**
 * Gateway override 토글 (기본 접힘).
 * gateway 는 평소 verse env 에서 파생되고, 펼쳐서 수동 override 가능하다.
 * override(=verse env 와 불일치)면 접힌 헤더에 배지로 경고한다.
 */
export function GatewaySelector() {
  const {
    manager,
    activeVerseId,
    switching,
    setGatewayOverride,
    setActive,
    setSwitching,
    setConnectionError,
  } = useActiveVerseStore();
  const effective = useActiveVerseStore(selectEffectiveGatewayId);
  const [, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  // verse env 에서 파생되는 기본 gateway — effective 와 다르면 override 상태.
  const verseDerived: GatewayId = activeVerseId
    ? gatewayIdForVerseEnv(resolveVerseEntry(activeVerseId).env)
    : effective;
  const isOverride = effective !== verseDerived;
  const effectiveLabel = findGateway(effective)?.label ?? effective;

  const handleSelect = async (id: GatewayId) => {
    if (id === effective) return;
    // verse env 기본값과 같으면 override 해제, 다르면 override 설정.
    const override = id === verseDerived ? null : id;
    setGatewayOverride(override);
    if (override) window.localStorage.setItem(GATEWAY_STORAGE_KEY, override);
    else window.localStorage.removeItem(GATEWAY_STORAGE_KEY);
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('gateway', id);
      return next;
    });

    // active verse 가 없으면(초기 connecting) override 만 갱신 — 다음 연결에 반영.
    if (!manager || !activeVerseId) return;

    setSwitching(true);
    setConnectionError(null);
    try {
      await manager.switchTo(activeVerseId, gatewayUrlOf(id));
      setActive(activeVerseId);
    } catch (err) {
      console.error('[GatewaySelector] gateway switch failed:', err);
      setActive(null);
      setConnectionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>Gateway: {effectiveLabel}</span>
        {isOverride && (
          <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px] leading-none">
            override
          </Badge>
        )}
      </button>
      {open && (
        <div className="flex gap-1">
          {GATEWAYS.map((g) => (
            <Button
              key={g.id}
              type="button"
              size="sm"
              variant={g.id === effective ? 'default' : 'outline'}
              disabled={switching}
              onClick={() => void handleSelect(g.id)}
              className="flex-1"
            >
              {g.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
